/**
 * OS08 — CHECKLIST PROBLEMA
 * "Esse cansaço sem crescimento é o sinal."
 * 10s | 720x900 | SEM AUDIO
 */

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  Easing,
  Sequence,
} from "remotion";
import {CLAMP} from "../../presets/idv";

const T = {
  dark: { bg: "#0D1117", headline: "#FFFFFF", body: "#C8C4C0", muted: "#888888" },
  accent: "#00FF87",
  accentBg: "rgba(0,255,135,0.12)",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";

const SZ = { top: 70, sides: 50, bottom: 80 };
const CW = 720 - SZ.sides * 2;

const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

const GridPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.03 + Math.sin(frame * 0.02) * 0.005;
  return (
    <div style={{
      position: "absolute", inset: 0,
      backgroundImage: `linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)`,
      backgroundSize: "40px 40px", pointerEvents: "none",
    }} />
  );
};

const CircuitPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.5 + Math.sin(frame * 0.02) * 0.1;
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `repeating-linear-gradient(0deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px), repeating-linear-gradient(90deg, rgba(0,255,135,${opacity * 0.03}) 0px, transparent 1px, transparent 59px, rgba(0,255,135,${opacity * 0.03}) 60px)`,
      backgroundSize: "60px 60px",
    }} />
  );
};

const SeriesLabel: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", top: 30, left: SZ.sides,
      opacity: fadeIn(frame, 0, 10),
      fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500, letterSpacing: 2,
      textTransform: "uppercase", color: T.dark.muted,
    }}>CONTENT OS</div>
  );
};

const Headline: React.FC<{children: React.ReactNode; size?: number; color?: string; delay?: number}> =
  ({children, size = 48, color = T.dark.headline, delay = 0}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)}px)`,
      fontFamily: FONT_SERIF, fontSize: size, fontWeight: 700, lineHeight: 1.15, color, maxWidth: CW,
    }}>{children}</div>
  );
};

const Footer: React.FC<{handle?: string; slide?: string}> = ({handle = "@guilhermeribeiro.me", slide = "8/10"}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", bottom: 40, left: SZ.sides, right: SZ.sides,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      opacity: fadeIn(frame, 40, 50),
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 400, color: T.dark.muted}}>{handle}</span>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 500, color: T.dark.muted}}>{slide}</span>
    </div>
  );
};

const SlideNumber: React.FC<{n: string}> = ({n}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute", bottom: 70, right: SZ.sides,
      fontFamily: FONT_SERIF, fontSize: 120, fontWeight: 800, color: "#FFFFFF",
      opacity: interpolate(frame, [10, 25], [0, 0.06], {extrapolateRight: CLAMP}),
      lineHeight: 1, userSelect: "none",
    }}>{n}</div>
  );
};

const CHECKLIST_ITEMS = [
  "Trocar de ferramenta toda semana",
  "Medir só curtida, nao conversao",
  "Postar sem sistema que aprende",
  "Esforco sem retorno composto",
];

export const OS08ChecklistProblema: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{backgroundColor: T.dark.bg}}>
      <Sequence from={0} durationInFrames={300}>
        <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
          <GridPattern />
          <CircuitPattern />
          <SeriesLabel />
          <Headline size={48} delay={5}>
            Esse cansaço sem crescimento é o sinal.
          </Headline>
          <div style={{marginTop: 24, display: "flex", flexDirection: "column", gap: 12}}>
            {CHECKLIST_ITEMS.map((item, i) => (
              <div key={i} style={{
                opacity: fadeIn(frame, 20 + i * 10, 30 + i * 10),
                transform: `translateY(${slideUp(frame, 20 + i * 10, 30 + i * 10)}px)`,
                display: "flex", alignItems: "flex-start", gap: 12,
              }}>
                <span style={{
                  fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 700, color: T.accent, lineHeight: 1.3, flexShrink: 0,
                }}>x</span>
                <span style={{
                  fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 400, color: T.dark.body, lineHeight: 1.4,
                }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{
            opacity: fadeIn(frame, 80, 90),
            transform: `translateY(${slideUp(frame, 80, 90)}px)`,
            marginTop: 28,
            fontFamily: FONT_MONO, fontSize: 16, fontWeight: 700, color: T.accent, letterSpacing: 1,
          }}>
            FALTA SISTEMA, não esforço.
          </div>
          <SlideNumber n="08" />
          <Footer handle="@guilhermeribeiro.me" slide="8/10" />
        </AbsoluteFill>
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
